import { useCallback, useRef } from 'react'
import CharterBundleCalendarModal from './CharterBundleCalendarModal'
import type { FareClassWithReward } from 'constants/flight'
import type { OfferPageRoom } from 'contexts/OfferPage/offerPageStateReducer'
import { OfferPageActions } from 'contexts/OfferPage/offerPageStateReducer'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import type { DateRange } from 'components/MaldivesDirect/MaldivesDirectSearchPage/hooks/types'
import { useScreenSizeOnly } from 'hooks/useScreenSize'
import withOfferPageState from 'contexts/OfferPage/withOfferPageState'
import CharterFlightCalendarSelectBox from './CharterFlightCalendarSelectBox'

interface Props {
  selectedMonth: string
  onMonthChange: (month: string) => void
  nights: number
  fareClass: FareClassWithReward
  onDurationChange: (nights: number) => void
  durationOptions: ReadonlyArray<{ value: number, label: string }>
  checkInDate?: string
  charterBundleRoute?: App.CharterBundleRoute
  rooms: Array<OfferPageRoom>
  className?: string
  dateRangesByMonth: Record<string, Array<DateRange>>
  open: boolean
  feederOriginAirportCode?: string
}

/**
 * Charter Flight Calendar Select for Desktop
 * Uses ModalContext pattern to open the calendar modal
 */
function CharterFlightCalendarSelect(props: Props) {
  const {
    selectedMonth,
    onMonthChange,
    onDurationChange,
    durationOptions,
    className,
    dateRangesByMonth,
    open,
    checkInDate,
    nights,
    charterBundleRoute,
    rooms,
    fareClass,
    feederOriginAirportCode,
  } = props

  const dispatch = useOfferPageDispatch()
  const isMobile = useScreenSizeOnly('mobile')

  const onClose = useCallback(() => {
    dispatch({ type: OfferPageActions.setRoomType, room: undefined })
    dispatch({ type: OfferPageActions.closeFilter })
  }, [dispatch])

  const handleFareClassChange = useCallback((selectedClass: FareClassWithReward) => {
    dispatch({ type: OfferPageActions.setFareClass, fareClass: selectedClass })
  }, [dispatch])

  const handleDateSelect = useCallback((option: { departDate: string, returnDate: string, checkIn: string, checkOut: string }) => {
    // Update dates in offer page state
    dispatch({
      type: OfferPageActions.setDates,
      checkIn: option.checkIn,
      checkOut: option.checkOut,
    })
    // Close filter after selecting dates
    onClose()
  }, [dispatch, onClose])

  const triggerRef = useRef<HTMLButtonElement>(null)

  return (<div className={className}>
    <CharterFlightCalendarSelectBox
      ref={triggerRef}
      checkInDate={checkInDate}
      nights={nights}
      active={open}
      charterBundleRoute={charterBundleRoute}
      rooms={rooms}
      fareClass={fareClass}
      feederOriginAirportCode={feederOriginAirportCode}
    />
    <CharterBundleCalendarModal
      isOpen={open && !isMobile}
      onClose={onClose}
      onDateSelect={handleDateSelect}
      selectedMonth={selectedMonth}
      onMonthChange={onMonthChange}
      onDurationChange={onDurationChange}
      durationOptions={durationOptions}
      onFareClassChange={handleFareClassChange}
      dateRangesByMonth={dateRangesByMonth}
    />
  </div>
  )
}

export default withOfferPageState(state => ({
  open: state.activeFilter === 'checkin',
  feederOriginAirportCode: state.airport?.code,
}), CharterFlightCalendarSelect)
